import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { apiService } from '../services/api';
import { useAuth } from './AuthContext';

interface OrderCounts {
  pending: number;
  processing: number;
  picking: number;
  packing: number;
  ready_to_ship: number;
  shipped: number;
  cancelled: number;
  on_hold: number;
  delivery_failed: number;
  returned: number;
  today_orders: number;
  total_revenue: number;
  total_orders: number;
  quote: number;
  backorder: number;
  folder_counts: {
    sydney_fleet?: number;
    sydney_fleet_bulk?: number;
    border_express?: number;
    border_express_bulk?: number;
    courier_please?: number;
    vellex?: number;
    mainfreight?: number;
    pickup?: number;
  };
  ready_to_pickup_counts: {
    sydney_fleet?: number;
    sydney_fleet_bulk?: number;
    border_express?: number;
    border_express_bulk?: number;
    courier_please?: number;
    vellex?: number;
    mainfreight?: number;
    pickup?: number;
  };
  dispatched_counts: {
    sydney_fleet?: number;
    sydney_fleet_bulk?: number;
    border_express?: number;
    border_express_bulk?: number;
    courier_please?: number;
    vellex?: number;
    mainfreight?: number;
    pickup?: number;
  };
}

interface OrderCountsContextType {
  counts: OrderCounts;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

const OrderCountsContext = createContext<OrderCountsContextType | undefined>(undefined);

export const useOrderCounts = () => {
  const context = useContext(OrderCountsContext);
  if (context === undefined) {
    throw new Error('useOrderCounts must be used within an OrderCountsProvider');
  }
  return context;
};

interface OrderCountsProviderProps {
  children: ReactNode;
}

export const OrderCountsProvider: React.FC<OrderCountsProviderProps> = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [counts, setCounts] = useState<OrderCounts>({
    pending: 0,
    processing: 0,
    picking: 0,
    packing: 0,
    ready_to_ship: 0,
    shipped: 0,
    cancelled: 0,
    on_hold: 0,
    delivery_failed: 0,
    returned: 0,
    today_orders: 0,
    total_revenue: 0,
    total_orders: 0,
    quote: 0,
    backorder: 0,
    folder_counts: {},
    ready_to_pickup_counts: {},
    dispatched_counts: {}
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchCounts = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.client.get('/orders/stats', {
        params: {
          _t: Date.now() // Cache busting parameter
        }
      });
      const backendData = response.data;
      
      // Handle both formats: direct counts or nested status_counts
      const statusCounts = backendData.status_counts || backendData;
      
      // Map backend status names to frontend expected names
      const mappedCounts = {
        pending: statusCounts.pending || 0,
        processing: statusCounts.processing || 0,
        picking: statusCounts.picking || 0,
        packing: statusCounts.packed || statusCounts.packing || 0, // Handle both 'packed' and 'packing'
        ready_to_ship: statusCounts.ready_to_ship || 0,
        shipped: statusCounts.shipped || 0,
        cancelled: statusCounts.cancelled || 0,
        on_hold: statusCounts.on_hold || 0,
        delivery_failed: statusCounts.delivery_failed || 0,
        returned: statusCounts.returned || 0,
        today_orders: backendData.today_orders || 0,
        total_revenue: backendData.total_revenue || 0,
        total_orders: backendData.total_orders || 0,
        quote: statusCounts.quote || 0,
        backorder: statusCounts.backorder || 0,
        folder_counts: backendData.folder_counts || {},
        ready_to_pickup_counts: backendData.ready_to_pickup_counts || {},
        dispatched_counts: backendData.dispatched_counts || {}
      };
      
      setCounts(mappedCounts);
    } catch (err: any) {
      console.error('Error fetching order counts:', err);
      setError(err.response?.data?.error || 'Failed to fetch order counts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated && user) {
      fetchCounts();
    }
  }, [isAuthenticated, user]);

  // Periodic refresh: the sidebar badges must track reality even when a
  // flow (label-print auto-dispatch, Process Run, Neto sync) doesn't fire
  // the orderStatusChanged event — stale badges read as "incorrect numbers"
  // (user report, 2026-10-19).
  useEffect(() => {
    if (!isAuthenticated || !user) return;
    const interval = setInterval(() => {
      fetchCounts();
    }, 60000);
    return () => clearInterval(interval);
  }, [isAuthenticated, user]);

  // Listen for order status changes
  useEffect(() => {
    const handleOrderStatusChange = () => {
      fetchCounts();
    };

    // Listen for custom events when orders are moved
    window.addEventListener('orderStatusChanged', handleOrderStatusChange);
    
    return () => {
      window.removeEventListener('orderStatusChanged', handleOrderStatusChange);
    };
  }, []);

  const value: OrderCountsContextType = {
    counts,
    loading,
    error,
    refetch: fetchCounts
  };

  return (
    <OrderCountsContext.Provider value={value}>
      {children}
    </OrderCountsContext.Provider>
  );
};
